import { I9nSchemaBySystem } from '@biteinc/common';
import { EnvironmentHelper } from '@biteinc/enums';
import { I9nHelper } from '@biteinc/helpers';
export function getCopyableFieldNamesFromReferenceIntegration(system, timesSynced) {
    const schema = I9nSchemaBySystem[system];
    return Object.entries(schema.fields)
        .filter(([_fieldName, fieldSchema]) => canCopyFieldWithSchema(fieldSchema, timesSynced))
        .map(([fieldName, _fieldSchema]) => fieldName)
        .concat(getGenericFieldsForSystem(system).filter((fieldName) => {
        switch (fieldName) {
            case 'notes':
            case 'vendorId':
            case 'disableSync':
            case 'skipNonSyncOperations':
            case 'disable':
                return false;
            case 'diningOptions':
                // make sure that dining options on this integration don't contain any fields that we
                // can't copy
                if ((schema.type === 'pos' || schema.type === 'fulfillment') &&
                    schema.diningOptionFields) {
                    return Object.values(schema.diningOptionFields).every((fieldSchema) => {
                        return canCopyFieldWithSchema(fieldSchema, timesSynced);
                    });
                }
                throw new Error(`No other integration type has dining option fields.`);
            default:
                // none of the generic fields require a sync to have occurred
                return timesSynced === 0;
        }
    }));
}
function canCopyFieldWithSchema(fieldSchema, timesSynced) {
    // We can't set these values on creation
    if ((timesSynced === 1 && !fieldSchema.isSetAfterFirstSync) ||
        (fieldSchema.isSetAfterFirstSync && timesSynced !== 1)) {
        return false;
    }
    if ((timesSynced === 2 && !fieldSchema.isSetAfterSecondSync) ||
        (fieldSchema.isSetAfterSecondSync && timesSynced !== 2)) {
        return false;
    }
    if ((timesSynced === 3 && !fieldSchema.isSetAfterThirdSync) ||
        (fieldSchema.isSetAfterThirdSync && timesSynced !== 3)) {
        return false;
    }
    if (timesSynced > 3) {
        return false;
    }
    // This is the one field that must be filled out
    if (fieldSchema.identifiesTheLocation) {
        return false;
    }
    if (fieldSchema.isHidden || fieldSchema.isReadOnly) {
        return false;
    }
    return true;
}
export function getCopyableDiningOptionFieldNamesFromReferenceIntegration(system, timesSynced) {
    const schema = I9nSchemaBySystem[system];
    if (!((schema.type === 'pos' || schema.type === 'fulfillment') && schema.diningOptionFields)) {
        // No dining option fields => no copyable dining option fields
        return [];
    }
    return Object.entries(schema.diningOptionFields)
        .filter(([, fieldSchema]) => {
        return canCopyFieldWithSchema(fieldSchema, timesSynced);
    })
        .map(([fieldName, _fieldSchema]) => fieldName);
}
export function getGenericFieldsForSystem(system) {
    const i9nSchema = I9nSchemaBySystem[system];
    const allowedGenericFields = ['notes', 'vendorId'];
    if ((i9nSchema.type === 'pos' || i9nSchema.type === 'fulfillment') &&
        i9nSchema.diningOptionFields) {
        allowedGenericFields.push('diningOptions');
    }
    if (i9nSchema.type === 'pos') {
        if (i9nSchema.syncsSections) {
            allowedGenericFields.push('syncSections', 'preserveSectionItemOrder', 'menuSectionItemSortPriority', 'sectionSortPrioritiesApplyTo');
        }
        if (i9nSchema.syncsMenuStructures) {
            allowedGenericFields.push('syncMenuStructures');
        }
        if (i9nSchema.syncsBadges) {
            allowedGenericFields.push('syncBadges');
        }
        allowedGenericFields.push('preserveModGroupModOrder', 'preserveItemModGroupOrder', 'modGroupModSortPriority', 'maxSendOrderConcurrency');
    }
    if (I9nHelper.systemCanBeSynced(system)) {
        allowedGenericFields.push('disableSync');
    }
    if (i9nSchema.type === 'pos' || i9nSchema.type === 'loyalty') {
        allowedGenericFields.push('skipNonSyncOperations');
    }
    if (i9nSchema.type === 'loyalty') {
        allowedGenericFields.push('enableMultipleRewardRedemption');
    }
    if (i9nSchema.type === 'stored-value') {
        allowedGenericFields.push('useAsSecondaryPayment');
        allowedGenericFields.push('label');
        allowedGenericFields.push('icons');
    }
    if (i9nSchema.type !== 'pos' &&
        i9nSchema.type !== 'pos-data-source' &&
        i9nSchema.type !== 'supplementary-menu-source' &&
        i9nSchema.type !== 'kiosk-payment' &&
        i9nSchema.type !== 'ecomm-payment') {
        allowedGenericFields.push('disable');
    }
    if (('supportsOrderStatusPolling' in i9nSchema || 'supportsOrderStatusWebhooks' in i9nSchema) &&
        (i9nSchema.supportsOrderStatusPolling || i9nSchema.supportsOrderStatusWebhooks)) {
        allowedGenericFields.push('notifyUserOfOrderStatusUpdates');
        if (i9nSchema.supportsOrderStatusPolling && i9nSchema.supportsOrderStatusWebhooks) {
            allowedGenericFields.push('useWebhooksForOrderStatuses');
            allowedGenericFields.push('sendOrderStatusCheckup');
        }
    }
    if (i9nSchema.type === 'supplementary-menu-source') {
        allowedGenericFields.push('syncPriority');
    }
    if ('syncsDiscounts' in i9nSchema && i9nSchema.syncsDiscounts) {
        allowedGenericFields.push('loyaltyDiscountId');
    }
    if (i9nSchema.canUsePinata && EnvironmentHelper.isCurrentDev()) {
        allowedGenericFields.push('pinataApiServer');
    }
    return allowedGenericFields;
}
//# sourceMappingURL=integration_helper.js.map